import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Text,
  HStack,
  VStack,
  Button,
  Select,
  Code,
  Input,
  InputGroup,
  InputLeftElement,
  Collapse,
  IconButton,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Card,
  CardBody,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Divider,
} from '@chakra-ui/react';
import { FiRefreshCw, FiSearch, FiChevronDown, FiChevronUp, FiEye, FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const API_BASE = '/api';
const ITEMS_PER_PAGE = 20;

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchLogs = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/audit-logs/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setLogs(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'blue';
      case 'delete': return 'red';
      case 'login': return 'purple';
      case 'logout': return 'gray';
      default: return 'gray';
    }
  };

  const getModelColor = (model) => {
    switch (model) {
      case 'customer': return 'teal';
      case 'subscription': return 'cyan';
      case 'payment': return 'green';
      case 'refund': return 'orange';
      case 'user': return 'blue';
      case 'settings': return 'gray';
      case 'api_key': return 'purple';
      case 'webhook': return 'pink';
      default: return 'gray';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return '+';
      case 'update': return '✎';
      case 'delete': return '✕';
      default: return '•';
    }
  };

  const toggleRowExpansion = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const openDetailModal = (log) => {
    setSelectedLog(log);
    onOpen();
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    try {
      const parsed = typeof changes === 'string' ? JSON.parse(changes) : changes;
      return parsed;
    } catch {
      return changes;
    }
  };

  const renderChangeValue = (value) => {
    if (value === null || value === undefined) return <Text color="gray.400" as="span">null</Text>;
    if (typeof value === 'boolean') return <Badge colorScheme={value ? 'green' : 'red'}>{value.toString()}</Badge>;
    if (typeof value === 'object') return <Code fontSize="xs" display="block" whiteSpace="pre-wrap">{JSON.stringify(value, null, 2)}</Code>;
    return <Text as="span">{String(value)}</Text>;
  };

  const renderChangesPreview = (changes) => {
    const parsed = formatChanges(changes);
    if (!parsed) return <Text color="gray.400" fontSize="sm">No changes recorded</Text>;
    
    if (typeof parsed === 'object' && !Array.isArray(parsed)) {
      const keys = Object.keys(parsed);
      if (keys.length === 0) return <Text color="gray.400" fontSize="sm">No changes</Text>;
      return (
        <Text fontSize="sm" color="gray.600">
          {keys.length} field{keys.length !== 1 ? 's' : ''} changed: {keys.slice(0, 3).join(', ')}
          {keys.length > 3 && ` +${keys.length - 3} more`}
        </Text>
      );
    }
    return <Text fontSize="sm" color="gray.600">{String(parsed).substring(0, 50)}...</Text>;
  };

  const renderChangesDetail = (changes) => {
    const parsed = formatChanges(changes);
    if (!parsed) return <Text color="gray.400">No changes recorded</Text>;

    if (typeof parsed === 'object' && !Array.isArray(parsed)) {
      return (
        <VStack align="stretch" spacing={2}>
          {Object.entries(parsed).map(([key, value]) => (
            <Box key={key} p={2} bg="gray.50" borderRadius="md">
              <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase">{key}</Text>
              {typeof value === 'object' && value !== null && 'old' in value && 'new' in value ? (
                <HStack spacing={2} mt={1}>
                  <Box flex={1} p={2} bg="red.50" borderRadius="sm">
                    <Text fontSize="xs" color="red.600" fontWeight="medium">Before:</Text>
                    {renderChangeValue(value.old)}
                  </Box>
                  <Text color="gray.400">→</Text>
                  <Box flex={1} p={2} bg="green.50" borderRadius="sm">
                    <Text fontSize="xs" color="green.600" fontWeight="medium">After:</Text>
                    {renderChangeValue(value.new)}
                  </Box>
                </HStack>
              ) : (
                <Box mt={1}>{renderChangeValue(value)}</Box>
              )}
            </Box>
          ))}
        </VStack>
      );
    }
    
    return <Code p={3} display="block" whiteSpace="pre-wrap" fontSize="sm">{JSON.stringify(parsed, null, 2)}</Code>;
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesType = typeFilter === 'all' || log.model_type === typeFilter;
    const matchesAction = actionFilter === 'all' || log.action === actionFilter;
    const matchesSearch = !searchQuery || 
      (log.username && log.username.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (log.object_name && log.object_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (log.ip_address && log.ip_address.includes(searchQuery));
    return matchesType && matchesAction && matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, actionFilter, searchQuery]);

  // Calculate stats
  const stats = {
    total: logs.length,
    creates: logs.filter(l => l.action === 'create').length,
    updates: logs.filter(l => l.action === 'update').length,
    deletes: logs.filter(l => l.action === 'delete').length,
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Audit Log</Heading>
          <Text color="gray.500">Track all system changes and user activity</Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="teal" onClick={fetchLogs}>
          Refresh
        </Button>
      </HStack>

      {/* Stats Cards */}
      <StatGroup mb={6}>
        <Card flex={1} mr={4}>
          <CardBody>
            <Stat>
              <StatLabel>Total Events</StatLabel>
              <StatNumber color="teal.600">{stats.total}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card flex={1} mr={4}>
          <CardBody>
            <Stat>
              <StatLabel>Creates</StatLabel>
              <StatNumber color="green.500">{stats.creates}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card flex={1} mr={4}>
          <CardBody>
            <Stat>
              <StatLabel>Updates</StatLabel>
              <StatNumber color="blue.500">{stats.updates}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card flex={1}>
          <CardBody>
            <Stat>
              <StatLabel>Deletes</StatLabel>
              <StatNumber color="red.500">{stats.deletes}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </StatGroup>

      {/* Filters */}
      <HStack mb={6} spacing={4} flexWrap="wrap">
        <InputGroup maxW="300px">
          <InputLeftElement>
            <FiSearch color="gray.400" />
          </InputLeftElement>
          <Input 
            placeholder="Search by user, object, or IP..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="white"
          />
        </InputGroup>
        <Select 
          value={typeFilter} 
          onChange={(e) => setTypeFilter(e.target.value)} 
          w="180px"
          bg="white"
        >
          <option value="all">All Types</option>
          <option value="customer">Customers</option>
          <option value="subscription">Subscriptions</option>
          <option value="payment">Payments</option>
          <option value="refund">Refunds</option>
          <option value="user">Users</option>
          <option value="settings">Settings</option>
          <option value="api_key">API Keys</option>
          <option value="webhook">Webhooks</option>
        </Select>
        <Select 
          value={actionFilter} 
          onChange={(e) => setActionFilter(e.target.value)} 
          w="150px"
          bg="white"
        >
          <option value="all">All Actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
        </Select>
        {(searchQuery || typeFilter !== 'all' || actionFilter !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { setSearchQuery(''); setTypeFilter('all'); setActionFilter('all'); }}
          >
            Clear Filters
          </Button>
        )}
        <Text color="gray.500" fontSize="sm" ml="auto">
          Showing {paginatedLogs.length} of {filteredLogs.length} entries
        </Text>
      </HStack>

      {/* Logs Table */}
      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th w="40px"></Th>
              <Th>Time</Th>
              <Th>User</Th>
              <Th>Action</Th>
              <Th>Type</Th>
              <Th>Object</Th>
              <Th>Changes</Th>
              <Th>IP Address</Th>
              <Th w="50px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedLogs.map(log => (
              <React.Fragment key={log.id}>
                <Tr 
                  _hover={{ bg: 'gray.50' }} 
                  cursor="pointer"
                  onClick={() => toggleRowExpansion(log.id)}
                >
                  <Td>
                    <IconButton
                      icon={expandedRows.has(log.id) ? <FiChevronUp /> : <FiChevronDown />}
                      size="xs"
                      variant="ghost"
                      aria-label="Expand row"
                    />
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {new Date(log.created_at).toLocaleDateString()}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <HStack>
                      <Box 
                        w={8} h={8} 
                        borderRadius="full" 
                        bg="teal.100" 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center"
                      >
                        <Text fontSize="sm" fontWeight="bold" color="teal.700">
                          {(log.username || 'S')[0].toUpperCase()}
                        </Text>
                      </Box>
                      <Text fontWeight="medium">{log.username || 'System'}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <Badge 
                      colorScheme={getActionColor(log.action)}
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      <HStack spacing={1}>
                        <Text>{getActionIcon(log.action)}</Text>
                        <Text textTransform="capitalize">{log.action}</Text>
                      </HStack>
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={getModelColor(log.model_type)} variant="subtle">
                      {log.model_type}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm" fontWeight="medium">
                      {log.object_name || '-'}
                    </Text>
                    {log.object_id && (
                      <Text fontSize="xs" color="gray.500">ID: {log.object_id}</Text>
                    )}
                  </Td>
                  <Td maxW="200px">
                    {renderChangesPreview(log.changes)}
                  </Td>
                  <Td>
                    <Code fontSize="xs" px={2} py={1}>{log.ip_address || '-'}</Code>
                  </Td>
                  <Td>
                    <Tooltip label="View Details">
                      <IconButton
                        icon={<FiEye />}
                        size="sm"
                        variant="ghost"
                        colorScheme="teal"
                        onClick={(e) => { e.stopPropagation(); openDetailModal(log); }}
                        aria-label="View details"
                      />
                    </Tooltip>
                  </Td>
                </Tr>
                <Tr>
                  <Td colSpan={9} p={0} border="none">
                    <Collapse in={expandedRows.has(log.id)} animateOpacity>
                      <Box p={4} bg="gray.50" borderBottomWidth={1}>
                        <Text fontWeight="semibold" mb={3} color="gray.700">Change Details</Text>
                        {renderChangesDetail(log.changes)}
                      </Box>
                    </Collapse>
                  </Td>
                </Tr>
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
        
        {paginatedLogs.length === 0 && (
          <Text color="gray.500" textAlign="center" py={12}>
            No audit logs found matching your filters
          </Text>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Flex justify="space-between" align="center" p={4} borderTopWidth={1}>
            <Text fontSize="sm" color="gray.500">
              Page {currentPage} of {totalPages}
            </Text>
            <HStack>
              <IconButton
                icon={<FiChevronLeft />}
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                isDisabled={currentPage === 1}
                aria-label="Previous page"
              />
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    variant={currentPage === pageNum ? 'solid' : 'ghost'}
                    colorScheme={currentPage === pageNum ? 'teal' : 'gray'}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <IconButton
                icon={<FiChevronRight />}
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                isDisabled={currentPage === totalPages}
                aria-label="Next page"
              />
            </HStack>
          </Flex>
        )}
      </Box>

      {/* Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Badge colorScheme={getActionColor(selectedLog?.action)} fontSize="md" px={3} py={1}>
                {selectedLog?.action}
              </Badge>
              <Badge colorScheme={getModelColor(selectedLog?.model_type)} fontSize="md" px={3} py={1}>
                {selectedLog?.model_type}
              </Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedLog && (
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Box>
                    <Text fontSize="sm" color="gray.500">Object</Text>
                    <Text fontWeight="medium">{selectedLog.object_name || selectedLog.object_id || '-'}</Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="sm" color="gray.500">Object ID</Text>
                    <Code>{selectedLog.object_id || '-'}</Code>
                  </Box>
                </HStack>
                
                <Divider />
                
                <HStack justify="space-between">
                  <Box>
                    <Text fontSize="sm" color="gray.500">User</Text>
                    <Text fontWeight="medium">{selectedLog.username || 'System'}</Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="sm" color="gray.500">IP Address</Text>
                    <Code>{selectedLog.ip_address || '-'}</Code>
                  </Box>
                </HStack>
                
                <Box>
                  <Text fontSize="sm" color="gray.500">Timestamp</Text>
                  <HStack>
                    <FiCalendar />
                    <Text>{new Date(selectedLog.created_at).toLocaleString()}</Text>
                  </HStack>
                </Box>
                
                <Divider />
                
                <Box>
                  <Text fontWeight="semibold" mb={3}>Changes</Text>
                  {renderChangesDetail(selectedLog.changes)}
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default AuditLog;
